import { useQuery } from "@tanstack/react-query";
import { motion, AnimatePresence } from "framer-motion";
import { axiosget } from "../../helpers/httprequest";
import { Alert, AlertDescription } from "../../components/ui/Alert";
import { Skeleton } from "../../components/ui/Skeleton";
import EmptyState from "../../components/ui/EmptyState";
import TblReferee from "../../table/TblReferee";
import BtnReferee from "../../buttons/BtnReferee";
import PageHeader from "../../components/PageHeader";

const UsersIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
  </svg>
);

const RefereeLoadingSkeleton = () => (
  <div className="space-y-4">
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {[1, 2].map((i) => (
        <div key={i} className="space-y-3 p-4 border border-neutral-200 rounded-lg">
          <Skeleton className="h-6 w-3/4" />
          <Skeleton className="h-4 w-1/2" />
          <Skeleton className="h-4 w-2/3" />
          <Skeleton className="h-4 w-full" />
        </div>
      ))}
    </div>
  </div>
);

const Referee = ({ wizardMode = false }) => {
  const { isLoading, error, data } = useQuery({
    queryKey: ["listOfReferees"],
    queryFn: async () => {
      const req = await axiosget("ms_applicant_referee");
      return req;
    },
  });

  const hasData = data?.message === undefined && data?.data && data.data.length > 0;
  const isEmpty = data?.message === undefined && data?.data && data.data.length === 0;
  const hasError = error || data?.message;

  return (
    <>
      {!wizardMode ? (
        <PageHeader
          title="Parents & Guardians"
          subtitle="Manage your parent and guardian information"
          icon={<UsersIcon />}
          action={!isLoading && <BtnReferee />}
        />
      ) : (
        !isLoading && <div className="flex justify-end mb-3"><BtnReferee /></div>
      )}

      {/* Loading State */}
      {isLoading && <RefereeLoadingSkeleton />}

      {/* Error State */}
      {hasError && !isLoading && (
        <Alert variant="danger">
          <AlertDescription>
            {error?.message || data?.message || "Unable to load parent/guardian information. Please try again later."}
          </AlertDescription>
        </Alert>
      )}

      {/* Empty State */}
      {isEmpty && !isLoading && !hasError && (
        <EmptyState
          variant="no-data"
          title="No Parents or Guardians Added"
          description="You haven't added any parent or guardian information yet. Click 'Add Guardian' above to get started."
          icon={<UsersIcon className="h-12 w-12" />}
        />
      )}

      {/* Data Display */}
      {hasData && !isLoading && !hasError && (
        <div>
          <p className="text-sm text-neutral-500 mb-4">
            {data.data.length} {data.data.length === 1 ? "parent/guardian" : "parents/guardians"} registered
          </p>
          <TblReferee data={data.data} />
        </div>
      )}
    </>
  );
};

export default Referee;
